import { useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useEffect, useState } from "react";

import { Organization, usePatchOrganization } from "../../../api/v1";

import { FlagDef } from "./types";

type UseEnablementFlagsOptions = {
  orgId: string | null;
  organization: Organization | undefined;
  isLoadingOrganization: boolean;
};

export function useEnablementFlags(options: UseEnablementFlagsOptions) {
  const { orgId, organization, isLoadingOrganization } = options;
  const queryClient = useQueryClient();
  const { mutateAsync: patchOrganizationAsync, isLoading: isPatchingOrganization } = usePatchOrganization();
  const [flags, setFlags] = useState<FlagDef[]>([]);
  const [pendingFlag, setPendingFlag] = useState<string | null>(null);

  useEffect(() => {
    if (!organization) return;

    const mappedFlags = Object.entries(organization as Record<string, unknown>)
      .filter((entry): entry is [string, boolean] => typeof entry[1] === "boolean" && !entry[0].includes("."))
      .map(([flag, value]) => ({ flag, value }));

    setFlags(mappedFlags);
  }, [organization]);

  const toggleFlag = async (flag: string, checked: boolean) => {
    if (!orgId) {
      message.warning("Select an organization");
      return;
    }

    const previousFlags = flags;
    setFlags((current) => current.map((def) => (def.flag === flag ? { ...def, value: checked } : def)));
    setPendingFlag(flag);

    try {
      const response = await patchOrganizationAsync({
        orgId,
        data: { configData: { [flag]: checked } },
      });

      if (!response?.success) {
        setFlags(previousFlags);
        message.error(response?.message ?? "Unable to update flag");
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ["organizationById", orgId] });
      message.success("Flag updated successfully");
    } catch {
      setFlags(previousFlags);
      message.error("Something went wrong while updating the flag");
    } finally {
      setPendingFlag(null);
    }
  };

  const addFlag = async (flag: string, value: boolean) => {
    if (!orgId) {
      message.warning("Select an organization");
      return false;
    }

    const trimmedFlag = flag.trim();
    if (!trimmedFlag) {
      message.warning("Flag is required");
      return false;
    }

    if (trimmedFlag.includes(".")) {
      message.warning("Flag must be a flat key without dots");
      return false;
    }

    if (flags.some((def) => def.flag === trimmedFlag)) {
      message.warning("Flag already exists");
      return false;
    }

    const previousFlags = flags;
    const nextFlag: FlagDef = {
      flag: trimmedFlag,
      value,
    };

    setFlags((current) => [...current, nextFlag]);

    try {
      const response = await patchOrganizationAsync({
        orgId,
        data: { configData: { [trimmedFlag]: value } },
      });

      if (!response?.success) {
        setFlags(previousFlags);
        message.error(response?.message ?? "Unable to add flag");
        return false;
      }

      await queryClient.invalidateQueries({ queryKey: ["organizationById", orgId] });
      message.success("Flag added successfully");
      return true;
    } catch {
      setFlags(previousFlags);
      message.error("Something went wrong while adding the flag");
      return false;
    }
  };

  return {
    flags,
    isLoadingFlags: isLoadingOrganization,
    pendingFlag,
    isAddingFlag: isPatchingOrganization && pendingFlag === null,
    toggleFlag,
    addFlag,
  };
}
